import React, { useState, useRef } from "react";
import {
  Box, Button, TextField, Typography,
  CircularProgress, Alert, GlobalStyles,
} from "@mui/material";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axiosInstance from "../../../services/axiosInstance";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LockResetOutlinedIcon from "@mui/icons-material/LockResetOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import logoImg  from "../../../../design-system/assets/brand/logo.png";
import teliWave from "../../../../design-system/assets/mascot/teli-wave.png";
import bgSky    from "../../../../design-system/assets/bg-sky.png";
import PasswordRequirements from "../../common/PasswordRequirements";
import { isPasswordValid } from "../../../utils/passwordPolicy";
import { LanguageSwitcherCompact } from "../../../i18n/LanguageSwitcher.jsx";
import { useTranslation } from "react-i18next";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    height: 44,
    borderRadius: "var(--r-md)",
    backgroundColor: "#fff",
    fontFamily: "var(--font-ui)",
    "& input": { fontFamily: "var(--font-ui)", fontSize: "var(--t-body)", color: "var(--ink-800)", padding: "0 14px" },
    "& fieldset": { borderColor: "var(--line-200)" },
    "&:hover fieldset": { borderColor: "var(--line-300)" },
    "&.Mui-focused fieldset": { borderColor: "var(--line-200)", borderWidth: "1px" },
  },
  "& .MuiFormHelperText-root": { fontFamily: "var(--font-ui)", fontSize: "var(--t-caption)", marginInline: 0 },
};

const btnSx = {
  height: 48, borderRadius: "var(--r-md)",
  background: "var(--grad-brand-135)",
  boxShadow: "0 8px 20px rgba(90,70,220,.28)",
  fontFamily: "var(--font-ui)", fontWeight: 600, fontSize: "var(--t-body)",
  color: "#fff", textTransform: "none", border: "none",
  "&:hover": { filter: "brightness(1.06)", boxShadow: "0 8px 20px rgba(90,70,220,.28)" },
  "&:active": { transform: "translateY(1px)", transition: "transform .1s" },
  "&.Mui-disabled": { background: "var(--grad-brand-135)", color: "rgba(255,255,255,.5)" },
};

export default function ForgotPassword() {
  const { t } = useTranslation("auth");
  const navigate  = useNavigate();
  const location  = useLocation();

  // Pre-fill from signin page if the user clicked "Forgot password?" while a username was typed
  const prefilled = location.state?.identifier || "";

  // step: "lookup" | "reset" | "done"
  const [step, setStep]                   = useState("lookup");
  const [identifier, setIdentifier]       = useState(prefilled);
  const [lookupError, setLookupError]     = useState("");
  const [lookupLoading, setLookupLoading] = useState(false);

  const [resetToken, setResetToken]   = useState("");
  const [code, setCode]               = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPw, setConfirmPw]     = useState("");
  const [resetError, setResetError]   = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef(null);

  const startCooldown = () => {
    setResendCooldown(60);
    clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setResendCooldown(s => { if (s <= 1) { clearInterval(cooldownRef.current); return 0; } return s - 1; });
    }, 1000);
  };

  const onRequestCode = async () => {
    if (!identifier.trim()) { setLookupError(t("errEnterEmail")); return; }
    setLookupError(""); setLookupLoading(true);
    try {
      const { data } = await axiosInstance.post("/auth/forgot-password", { identifier: identifier.trim() });
      setResetToken(data.resetToken || "");
      setStep("reset");
      startCooldown();
    } catch (e) {
      setLookupError(e.response?.data?.message || t("errVerificationFailed"));
    } finally {
      setLookupLoading(false);
    }
  };

  const onResend = async () => {
    try {
      const { data } = await axiosInstance.post("/auth/forgot-password", { identifier: identifier.trim() });
      setResetToken(data.resetToken || "");
      setResetError("");
      startCooldown();
    } catch {}
  };

  const onResetPassword = async () => {
    if (!code || code.length !== 6) { setResetError(t("errEnterCode")); return; }
    if (!isPasswordValid(newPassword)) { setResetError(t("errPasswordRequirements")); return; }
    if (newPassword !== confirmPw) { setResetError(t("errPasswordsMatch")); return; }
    if (!resetToken) { setResetError(t("errResetExpired")); return; }
    setResetError(""); setResetLoading(true);
    try {
      await axiosInstance.post("/auth/reset-password", { resetToken, code, newPassword });
      setStep("done");
    } catch (e) {
      setResetError(e.response?.data?.message || t("errVerificationFailed"));
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <>
    <GlobalStyles styles={`
      @keyframes teliFloat { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
      @media (prefers-reduced-motion: reduce) { .teli-float { animation: none !important; } }
    `} />
    <Box sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
      minHeight: "100dvh",
      position: "relative",
      overflow: "hidden",
      backgroundImage: `url(${bgSky})`,
      backgroundSize: "cover",
      backgroundPosition: "center top",
      backgroundRepeat: "no-repeat",
    }}>

      {/* Language switcher — top-right corner of the full-page layout */}
      <Box sx={{ position: "absolute", top: 16, insetInlineEnd: 16, zIndex: 10 }}>
        <LanguageSwitcherCompact variant="light" />
      </Box>

      {/* LEFT PANEL */}
      <Box sx={{
        display: "grid", placeItems: "center end",
        padding: { xs: "24px", sm: "40px 24px 40px 40px" },
        overflowY: "auto", alignContent: "center",
        position: "relative", zIndex: 1,
      }}>
        <Box sx={{
          width: "100%", maxWidth: 420,
          background: "rgba(255,255,255,.96)",
          borderRadius: "var(--r-xl)",
          boxShadow: "0 16px 48px rgba(46,55,120,.14)",
          padding: "40px",
        }}>

          <Box component="img" src={logoImg} alt="Teleredo" role="button" tabIndex={0}
            sx={{ height: 34, width: "auto", mb: "24px", display: "block", cursor: "pointer" }}
            onClick={() => navigate("/")} onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && navigate("/")} />

          {/* ── STEP 1: Username lookup ── */}
          {step === "lookup" && (<>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
              <Box sx={{ width: 44, height: 44, borderRadius: "12px", flexShrink: 0,
                background: "rgba(79,70,229,0.1)",
                display: "flex", alignItems: "center", justifyContent: "center" }}>
                <LockResetOutlinedIcon sx={{ fontSize: 22, color: "#4F46E5" }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, color: "var(--ink-900)", letterSpacing: "-0.01em" }}>
                  {t("forgotPasswordTitle")}
                </Typography>
                <Typography sx={{ fontSize: "0.8125rem", color: "var(--ink-500)", lineHeight: 1.4 }}>
                  {t("aCodeWillBeSent")}
                </Typography>
              </Box>
            </Box>

            {lookupError && (
              <Alert severity="error" variant="outlined" onClose={() => setLookupError("")}
                sx={{ mb: 2, borderRadius: "var(--r-md)", fontSize: "var(--t-caption)" }}>
                {lookupError}
              </Alert>
            )}

            <Box sx={{ mb: "20px" }}>
              <Typography component="label" htmlFor="identifier" sx={{
                display: "block", fontSize: "var(--t-caption)", fontWeight: 600,
                color: "var(--ink-600)", mb: "6px" }}>{t("email")}</Typography>
              <TextField fullWidth hiddenLabel id="identifier" name="identifier" type="email"
                autoComplete="email" placeholder={t("emailPlaceholder")}
                value={identifier}
                onChange={e => { setIdentifier(e.target.value); setLookupError(""); }}
                onKeyDown={e => e.key === "Enter" && onRequestCode()}
                autoFocus={!prefilled} sx={inputSx}
                InputProps={{ startAdornment: (
                  <Box sx={{ pl: "12px", display: "flex", alignItems: "center" }}>
                    <MailOutlineIcon sx={{ fontSize: 18, color: "var(--ink-400)" }} />
                  </Box>
                ) }} />
              <Typography sx={{ mt: "6px", fontSize: "0.75rem", color: "var(--ink-400)" }}>
                {t("forgotPasswordDesc")}
              </Typography>
            </Box>

            <Button fullWidth variant="contained" disabled={lookupLoading} onClick={onRequestCode} sx={{ ...btnSx, mb: "20px" }}>
              {lookupLoading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : t("sendResetCode")}
            </Button>

            <Typography sx={{ textAlign: "center", fontSize: "0.84375rem", color: "var(--ink-500)" }}>
              {t("rememberedIt")}{" "}
              <Link to="/signin" style={{ color: "var(--brand-violet-ink)", fontWeight: 600, textDecoration: "none" }}>
                {t("backToSignIn")}
              </Link>
            </Typography>
          </>)}

          {/* ── STEP 2: OTP + New Password ── */}
          {step === "reset" && (<>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
              <Box sx={{ width: 44, height: 44, borderRadius: "12px", flexShrink: 0,
                background: "rgba(79,70,229,0.1)",
                display: "flex", alignItems: "center", justifyContent: "center" }}>
                <MarkEmailReadOutlinedIcon sx={{ fontSize: 22, color: "#4F46E5" }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, color: "var(--ink-900)", letterSpacing: "-0.01em" }}>
                  {t("checkYourEmail")}
                </Typography>
                <Typography sx={{ fontSize: "0.8125rem", color: "var(--ink-500)", lineHeight: 1.4 }}>
                  {t("codeSentToRegisteredEmail")}
                </Typography>
              </Box>
            </Box>

            {resetError && (
              <Alert severity="error" variant="outlined" onClose={() => setResetError("")}
                sx={{ mb: 2, borderRadius: "var(--r-md)", fontSize: "var(--t-caption)" }}>
                {resetError}
              </Alert>
            )}

            <Box sx={{ mb: "14px" }}>
              <Typography component="label" htmlFor="otp" sx={{
                display: "block", fontSize: "var(--t-caption)", fontWeight: 600,
                color: "var(--ink-600)", mb: "6px" }}>{t("verificationCode")}</Typography>
              <TextField fullWidth hiddenLabel id="otp" name="otp"
                inputProps={{ maxLength: 6, inputMode: "numeric", pattern: "[0-9]*" }}
                placeholder="000000" autoFocus
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                onKeyDown={e => e.key === "Enter" && document.getElementById("newPassword")?.focus()}
                sx={{
                  ...inputSx,
                  "& .MuiOutlinedInput-root input": {
                    textAlign: "center", letterSpacing: "0.35em",
                    fontSize: "1.375rem", fontWeight: 700, fontFamily: "monospace",
                  },
                }} />
            </Box>

            <Box sx={{ mb: "14px" }}>
              <Typography component="label" htmlFor="newPassword" sx={{
                display: "block", fontSize: "var(--t-caption)", fontWeight: 600,
                color: "var(--ink-600)", mb: "6px" }}>{t("newPassword")}</Typography>
              <TextField fullWidth hiddenLabel id="newPassword" name="newPassword" type="password"
                autoComplete="new-password" placeholder={t("minCharacters")}
                value={newPassword} onChange={e => setNewPassword(e.target.value)} sx={inputSx} />
              <PasswordRequirements password={newPassword} />
            </Box>

            <Box sx={{ mb: "20px" }}>
              <Typography component="label" htmlFor="confirmPw" sx={{
                display: "block", fontSize: "var(--t-caption)", fontWeight: 600,
                color: "var(--ink-600)", mb: "6px" }}>{t("confirmNewPassword")}</Typography>
              <TextField fullWidth hiddenLabel id="confirmPw" name="confirmPw" type="password"
                autoComplete="new-password" placeholder={t("repeatPassword")}
                value={confirmPw} onChange={e => setConfirmPw(e.target.value)}
                onKeyDown={e => e.key === "Enter" && onResetPassword()} sx={inputSx} />
            </Box>

            <Button fullWidth variant="contained" disabled={resetLoading || code.length !== 6}
              onClick={onResetPassword} sx={{ ...btnSx, mb: "16px" }}>
              {resetLoading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : t("setNewPassword")}
            </Button>

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Button size="small" onClick={() => { setStep("lookup"); setCode(""); setResetError(""); }}
                sx={{ fontSize: "0.8125rem", color: "var(--ink-500)", textTransform: "none", p: 0 }}>
                {t("changeEmail")}
              </Button>
              <Button size="small" disabled={resendCooldown > 0} onClick={onResend}
                sx={{ fontSize: "0.8125rem", color: resendCooldown > 0 ? "var(--ink-400)" : "var(--brand-violet-ink)", textTransform: "none", p: 0 }}>
                {resendCooldown > 0 ? t("resendIn", { count: resendCooldown }) : t("resendCode")}
              </Button>
            </Box>
          </>)}

          {/* ── STEP 3: Done ── */}
          {step === "done" && (<>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", py: 2 }}>
              <Box sx={{ width: 56, height: 56, borderRadius: "50%", mb: 2,
                background: "rgba(21,128,61,0.1)",
                display: "flex", alignItems: "center", justifyContent: "center" }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 30, color: "#15803D" }} />
              </Box>
              <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, color: "var(--ink-900)", mb: 0.5 }}>
                {t("passwordUpdated")}
              </Typography>
              <Typography sx={{ fontSize: "0.875rem", color: "var(--ink-500)", mb: 3 }}>
                {t("passwordChangedSuccess")}
              </Typography>
              <Button fullWidth variant="contained" onClick={() => navigate("/signin")} sx={btnSx}>
                {t("backToSignIn")}
              </Button>
            </Box>
          </>)}

        </Box>
      </Box>

      {/* RIGHT PANEL — mascot */}
      <Box sx={{
        display: { xs: "none", md: "grid" },
        placeItems: "end start",
        paddingLeft: "16px",
        overflow: "hidden",
        position: "relative", zIndex: 1,
      }}>
        <Box sx={{
          position: "absolute", top: "15%", left: "50%",
          transform: "translateX(-50%)", width: 560, height: 560, borderRadius: "50%",
          background: "radial-gradient(closest-side, rgba(122,63,228,0.45), transparent)",
          filter: "blur(90px)", opacity: 0.18, pointerEvents: "none", zIndex: 0,
        }} />
        <Box component="img" src={teliWave} alt="Teli, your Teleredo AI assistant"
          className="teli-float"
          sx={{
            position: "relative", zIndex: 2,
            height: "88vh", width: "auto", display: "block",
            filter: "drop-shadow(0 22px 34px rgba(46,55,120,.16))",
            animation: "teliFloat var(--float-loop, 6s) ease-in-out infinite",
          }} />
      </Box>

    </Box>
    </>
  );
}
